/**
 * The Office.js side of bookmarks: the one place in the add-in's new engine
 * that reads, lays, moves or deletes a bookmark through Word. Everything else
 * passes bookmark names through as the opaque address of a redline. Word grows,
 * shrinks and drops bookmarks as the document is edited; these operations are
 * how the tracker puts them back where the redline's paragraphs are.
 */
import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import { WordTokenizer } from "@common/utils/redline/text/word-tokenizer";

import { getExpandedRangeForBookmarks } from "../../document";
import { AnchorLog } from "../anchor-log";
import type { RecoveryConfig } from "../recovery-config";
import type { BookmarkedRangeRead, BookmarkEdgeMoves, BookmarkPlacement, BookmarkStates, DocumentBookmarks, WordParagraph } from "../word-document";

/** A requested bookmark and the first paragraph Office.js resolves it into, loaded with `isNullObject` and `uniqueLocalId`. */
interface BookmarkedParagraphLookup {
    bookmarkName: string;
    bookmarkedParagraph: Word.Paragraph;
}

export class OfficeDocumentBookmarks implements DocumentBookmarks {
    constructor(
        private readonly context: Word.RequestContext,
        private readonly config: RecoveryConfig,
    ) { }

    /** The bookmarks' range widened to whole paragraphs, the unit the proposal reads and rewrites. */
    async paragraphRange(bookmarkNames: string[]): Promise<Word.Range | null> {
        const range = await getExpandedRangeForBookmarks(this.context, bookmarkNames);
        /**
         * WHEN no bookmark name resolves to a live range
         *   CONSEQUENCE
         *     return null: there is no paragraph span to widen.
         */
        if (!range) return null;
        const paragraphs = range.paragraphs;
        paragraphs.load("items");
        await this.context.sync();
        /**
         * WHEN widening produced no paragraphs
         *   CONSEQUENCE
         *     keep Word's original range.
         *
         * WHY
         *   There is no first paragraph to seed the expandTo chain.
         */
        if (paragraphs.items.length === 0) return range;
        /**
         * EXAMPLE
         *   3 paragraphs in the widened span
         *     items[0]           -> paragraph 1
         *     loop step index 1  -> expand 1 to 2
         *     loop step index 2  -> expand 1 to 3
         */
        let expanded = paragraphs.items[0].getRange();
        for (let index = 1; index < paragraphs.items.length; index++) {
            const paragraphRange = paragraphs.items[index].getRange();
            expanded = expanded.expandTo(paragraphRange);
        }
        return expanded;
    }

    async readRange(bookmarkNames: string[]): Promise<BookmarkedRangeRead | null> {
        const { context } = this;
        const range = await this.paragraphRange(bookmarkNames);
        /**
         * WHEN the bookmark range is already lost
         *   CONSEQUENCE
         *     return null: there is no span to read for paragraph ids or neighbours.
         */
        if (!range) return null;
        const paragraphs = range.paragraphs;
        paragraphs.load("items/uniqueLocalId");
        const preceding = paragraphs.getFirst().getPreviousOrNullObject();
        const following = paragraphs.getLast().getNextOrNullObject();
        preceding.load("isNullObject,uniqueLocalId,text");
        following.load("isNullObject,uniqueLocalId,text");
        const firstParagraphCell = paragraphs.getFirst().parentTableCellOrNullObject;
        firstParagraphCell.load("isNullObject");
        const bookmarkedParagraphs = bookmarkNames.map((bookmarkName) => {
            const bookmarkedParagraph = context.document.getBookmarkRangeOrNullObject(bookmarkName).paragraphs.getFirstOrNullObject();
            bookmarkedParagraph.load("isNullObject,uniqueLocalId");
            return { bookmarkName, bookmarkedParagraph };
        });
        const ooxml = range.getOoxml();
        await context.sync();
        /**
         * The read every reader of the card's range gets: Word's export without the empty
         * paragraph Word ends it with, told by Word's own count of the range's paragraphs
         * (`OoxmlRange.readOfWordExport`), then the bookmarks Word dropped put back.
         *
         * EXAMPLE
         *   range        the cap paragraph alone (1 paragraph in `paragraphs.items`)
         *   Word's read  the cap paragraph, then Word's empty paragraph
         *   -> the cap paragraph alone, which the builder, the placement and the write all read
         */
        const wordExportRead = OoxmlRange.readOfWordExport(ooxml.value, paragraphs.items.length);
        const rangeOoxml = OfficeDocumentBookmarks.withDroppedBookmarksRestored(wordExportRead.packageOoxml, paragraphs.items, bookmarkedParagraphs);
        return {
            rangeOoxml,
            wordsExportAsGiven: wordExportRead.wordsExportAsGiven,
            paragraphIds: new OoxmlRange(rangeOoxml).paragraphIds(),
            uniqueLocalIds: paragraphs.items.map((paragraph) => paragraph.uniqueLocalId),
            /**
             * WHEN the span's first paragraph sits in a table cell
             *   CONSEQUENCE
             *     true, whatever `rangeOoxml` shows: Word Online reads a cell paragraph without its `w:tc`.
             */
            bookmarkedParagraphSitsInTableCell: !firstParagraphCell.isNullObject,
            /**
             * WHEN the bookmarked span starts at the document's first paragraph
             *   CONSEQUENCE
             *     previousParagraph is null.
             */
            previousParagraph: preceding.isNullObject ? null : { uniqueLocalId: preceding.uniqueLocalId, text: preceding.text },
            /**
             * WHEN the bookmarked span ends at the document's last paragraph
             *   CONSEQUENCE
             *     nextParagraph is null.
             */
            nextParagraph: following.isNullObject ? null : { uniqueLocalId: following.uniqueLocalId, text: following.text },
        };
    }

    /**
     * The range's OOXML with every requested bookmark Word left out of it put back on
     * the paragraph Office.js resolves the bookmark into.
     *
     * WHY
     *   Word Online's `getOoxml()` of an empty paragraph drops the zero-width bookmark
     *   naming it. The fold, the merge writer and the per-cell writer all find a card's
     *   paragraph by that bookmark, so the Base Rent cards on empty cells found none
     *   and wrote nothing.
     *
     * CASES
     *   - the read holds as many paragraphs as Word's range  -> each dropped bookmark is laid on the read paragraph
     *                                                           at the index of the paragraph Office.js resolves it into;
     *                                                           a body read holds as many once Word's closing empty
     *                                                           paragraph is left out (`readRange`)
     *   - the counts differ (a cell read with its whole row) -> the read is returned as given: its paragraphs
     *                                                           cannot be paired with Word's by position
     *   - no bookmark was dropped                            -> the read is returned as given
     *
     * EXAMPLE
     *   empty Annual cell in Word Online
     *     read        <w:p/>                       (1 paragraph, no bookmark)
     *     Word        items = [p#A]; _leg_letter_wire resolves into p#A -> index 0
     *     -> <w:p><w:bookmarkStart w:name="_leg_letter_wire"/><w:bookmarkEnd/></w:p>
     */
    private static withDroppedBookmarksRestored(readOoxml: string, rangeParagraphs: Word.Paragraph[], bookmarkedParagraphs: BookmarkedParagraphLookup[]): string {
        const read = new OoxmlRange(readOoxml);
        /**
         * WHEN the read and Word's range hold different numbers of paragraphs
         *   CONSEQUENCE
         *     return the read as given: a paragraph index in one is not an index in the other.
         */
        if (read.paragraphCount !== rangeParagraphs.length) return readOoxml;
        const rangeParagraphIds = rangeParagraphs.map((paragraph) => paragraph.uniqueLocalId);
        const paragraphIndexByBookmarkName = new Map(bookmarkedParagraphs.flatMap(({ bookmarkName, bookmarkedParagraph }) => {
            /**
             * CASES
             *   - the bookmark resolves to no paragraph            -> nothing to restore
             *   - it resolves to a paragraph outside the range     -> -1, nothing to restore
             *   - it resolves to a paragraph of the range          -> that paragraph's index
             */
            const paragraphIndex = bookmarkedParagraph.isNullObject ? -1 : rangeParagraphIds.indexOf(bookmarkedParagraph.uniqueLocalId);
            return paragraphIndex < 0 ? [] : [[bookmarkName, paragraphIndex] as const];
        }));
        const restoredNames = read.restoreDroppedBookmarks(paragraphIndexByBookmarkName);
        /**
         * WHEN no bookmark was put back
         *   CONSEQUENCE
         *     return the read as given, not a re-serialization of it.
         */
        if (restoredNames.length === 0) return readOoxml;
        return read.serialize();
    }

    /**
     * The row each bookmark's first paragraph sits in, as Word places it: the id of the
     * innermost table's first paragraph and the row's index in that table, one key per
     * bookmark Word finds in a table cell.
     *
     * WHY Word and not the read's markup
     *   Word Online's `getOoxml()` of a table-cell paragraph carries no `w:tc`, `w:tr` or
     *   `w:tbl`, so a page read there cannot say which row a cell is in. Apply All reads a
     *   cell as struck only where it can place it in a row a card deletes, so in Word
     *   Online an answer written into an empty cell of a struck row met no strike and
     *   landed in a row that then went (R31 review, 2026-10-16). Office.js places every
     *   cell, an empty one included, whatever its markup.
     *
     * WHY two syncs
     *   `getRange()` is no "or null object" method: queued on the null table of a body
     *   paragraph it can fail the sync on some hosts (`OfficeWordSession.readPage`). The
     *   first sync tells which paragraphs sit in a cell; only their tables are asked for
     *   a range.
     *
     * EXAMPLE
     *   _row1_reply_cell in row 1 of the table whose first paragraph is P-7, empty, read in Word Online
     *   -> Map { "_row1_reply_cell" => "P-7:1" }
     *
     * NULL
     *   A bookmark no longer in the document, or whose paragraph sits outside every
     *   table, has no entry.
     */
    async cellRowKeysOf(bookmarkNames: string[]): Promise<Map<string, string>> {
        const { context } = this;
        const probes = bookmarkNames.map((bookmarkName) => {
            const firstParagraph = context.document.getBookmarkRangeOrNullObject(bookmarkName).paragraphs.getFirstOrNullObject();
            const cell = firstParagraph.parentTableCellOrNullObject;
            cell.load("isNullObject,rowIndex");
            const table = firstParagraph.parentTableOrNullObject;
            table.load("isNullObject");
            return { bookmarkName, cell, table };
        });
        await context.sync();
        const cellProbes = probes
            .filter((probe) => !probe.cell.isNullObject && !probe.table.isNullObject)
            .map((probe) => {
                const tableFirstParagraph = probe.table.getRange().paragraphs.getFirstOrNullObject();
                tableFirstParagraph.load("isNullObject,uniqueLocalId");
                return { ...probe, tableFirstParagraph };
            });
        /** WHEN no bookmark sits in a cell CONSEQUENCE no table is asked for, and nothing is placed. */
        if (cellProbes.length === 0) return new Map();
        await context.sync();
        return new Map(cellProbes
            .filter((probe) => !probe.tableFirstParagraph.isNullObject)
            .map((probe) => [probe.bookmarkName, `${probe.tableFirstParagraph.uniqueLocalId}:${probe.cell.rowIndex}`]));
    }

    /**
     * Pulls a bookmark back off neighbouring paragraphs it has grown onto.
     *
     * Single paragraph: a bookmark that contains the paragraph mark is shrunk to
     * the content. Several paragraphs: when the first or last paragraph
     * contributes no visible text the bookmark is re-laid without it. Two
     * paragraphs that are both empty at the edges are left alone, since removing
     * both leaves nothing to bookmark.
     */
    async repairEdges(bookmarkNames: string[]): Promise<boolean> {
        const { context } = this;
        let anyBookmarkRelaid = false;
        for (const name of bookmarkNames) {
            const range = context.document.getBookmarkRangeOrNullObject(name);
            range.load("isNullObject");
            await context.sync();
            /**
             * WHEN the bookmark no longer resolves (its paragraph was deleted or cut)
             *   CONSEQUENCE
             *     continue before touching its paragraphs.
             *
             * WHY
             *   A null-object range's paragraph collection loads with no `items` at all,
             *   so reading its length throws "undefined is not an object" instead of
             *   letting the caller report the paragraph as gone.
             */
            if (range.isNullObject) continue;
            const paragraphs = range.paragraphs;
            paragraphs.load("items");
            await context.sync();
            /**
             * WHEN the bookmark resolves but encloses no paragraph
             *   CONSEQUENCE
             *     continue; there are no paragraph edges to repair.
             */
            if (paragraphs.items.length === 0) continue;
            /**
             * WHEN the bookmark touches exactly one paragraph
             *   CONSEQUENCE
             *     use the single-paragraph repair path.
             *
             * WHY
             *   The only repair here is shrinking off that paragraph mark, not trimming
             *   neighbour paragraphs.
             */
            if (paragraphs.items.length === 1) {
                /**
                 * EXAMPLE
                 *   items.length = 1
                 *     items[0]  -> the only paragraph
                 *     content   -> that paragraph without its paragraph mark
                 */
                const content = paragraphs.items[0].getRange(Word.RangeLocation.content);
                const relation = range.compareLocationWith(content);
                await context.sync();
                /**
                 * WHEN the bookmark contains the paragraph content range
                 *   CONSEQUENCE
                 *     re-lay it to the content range alone.
                 *
                 * WHY
                 *   In the one-paragraph path, this means the bookmark has grown to include the
                 *   paragraph mark.
                 */
                if (relation.value === Word.LocationRelation.contains) {
                    content.insertBookmark(name);
                    await context.sync();
                    anyBookmarkRelaid = true;
                    AnchorLog.record({ kind: "repair", bookmarkNames: [name], fix: "shrunk_to_paragraph_content", locationRelation: relation.value });
                }
                /**
                 * WHEN the single-paragraph path is done
                 *   CONSEQUENCE
                 *     continue to the next bookmark.
                 *
                 * WHY
                 *   The multi-paragraph trimming logic needs distinct first and last paragraphs.
                 */
                continue;
            }
            /**
             * EXAMPLE
             *   4 paragraphs in the span
             *     items[0]  -> paragraph 1
             *     first     -> the start edge checked for neighbour text
             */
            const first = paragraphs.items[0];
            /**
             * EXAMPLE
             *   items.length = 4
             *     length - 1  -> 3
             *     items[3]    -> paragraph 4
             *     last        -> the end edge checked for neighbour text
             */
            const last = paragraphs.items[paragraphs.items.length - 1];
            const firstPart = range.intersectWithOrNullObject(first.getRange());
            const lastPart = range.intersectWithOrNullObject(last.getRange());
            firstPart.load("isNullObject,text");
            lastPart.load("isNullObject,text");
            await context.sync();
            const trimmedStart = firstPart.isNullObject || visibleLength(firstPart.text) === 0;
            const trimmedEnd = lastPart.isNullObject || visibleLength(lastPart.text) === 0;
            const startEdgeNeedsNoTrimming = !trimmedStart;
            const endEdgeNeedsNoTrimming = !trimmedEnd;
            const neitherEdgeNeedsTrimming = startEdgeNeedsNoTrimming && endEdgeNeedsNoTrimming;
            /**
             * WHEN neither edge needs trimming
             *   CONSEQUENCE
             *     continue.
             *
             * WHY
             *   Both touched edge paragraphs still contain visible text.
             */
            if (neitherEdgeNeedsTrimming) continue;
            const startEdgeNeedsTrimming = trimmedStart;
            const endEdgeNeedsTrimming = trimmedEnd;
            const bookmarkSpansTwoParagraphs = paragraphs.items.length === 2;
            const bothEdgesNeedTrimmingInTwoParagraphSpan = startEdgeNeedsTrimming && endEdgeNeedsTrimming && bookmarkSpansTwoParagraphs;
            /**
             * WHEN both edge paragraphs are empty in a two-paragraph span
             *   CONSEQUENCE
             *     continue without trimming.
             *
             * WHY
             *   Trimming both boundaries would remove every paragraph from the bookmark.
             */
            if (bothEdgesNeedTrimmingInTwoParagraphSpan) continue;
            /**
             * CASES
             *   - trimmedStart        -> start at paragraphs.items[1]
             *   - not trimmedStart    -> keep the current range start
             *
             * EXAMPLE
             *   3 paragraphs in the span
             *     items[1]  -> paragraph 2
             */
            const startRange = trimmedStart ? paragraphs.items[1].getRange(Word.RangeLocation.start) : range.getRange(Word.RangeLocation.start);
            /**
             * CASES
             *   - trimmedEnd       -> end at paragraphs.items[length - 2]
             *   - not trimmedEnd   -> keep the current range end
             *
             * EXAMPLE
             *   items.length = 5
             *     length - 2  -> 3
             *     items[3]    -> paragraph 4
             */
            const endRange = trimmedEnd ? paragraphs.items[paragraphs.items.length - 2].getRange(Word.RangeLocation.end) : range.getRange(Word.RangeLocation.end);
            startRange.expandTo(endRange).insertBookmark(name);
            await context.sync();
            anyBookmarkRelaid = true;
            AnchorLog.record({
                kind: "repair",
                bookmarkNames: [name],
                fix: "edge_moved_off_neighbour",
                paragraphCount: paragraphs.items.length,
                trimmedStart,
                trimmedEnd,
                /**
                 * WHEN the bookmark does not intersect the first paragraph
                 *   CONSEQUENCE
                 *     firstPartText is null.
                 */
                firstPartText: firstPart.isNullObject ? null : AnchorLog.excerptStart(firstPart.text, AnchorLog.EXCERPT_LENGTH),
                /**
                 * WHEN the bookmark does not intersect the last paragraph
                 *   CONSEQUENCE
                 *     lastPartText is null.
                 */
                lastPartText: lastPart.isNullObject ? null : AnchorLog.excerptStart(lastPart.text, AnchorLog.EXCERPT_LENGTH),
            });
        }
        return anyBookmarkRelaid;
        /** Word reports a paragraph mark as text; an intersection that is only line breaks holds no real text. */
        function visibleLength(text: string): number {
            return text.replace(/[\r\n]/g, "").length;
        }
    }

    /** Re-lays the bookmark to start after `startAfterWord` and end before `endBeforeWord`, the words the span trim identified as neighbour text. */
    async moveEdgesToWords(bookmarkNames: string[], moves: BookmarkEdgeMoves): Promise<boolean> {
        const { startAfterWord, endBeforeWord } = moves;
        const startEdgeHasNoNeighbourWord = !startAfterWord;
        const endEdgeHasNoNeighbourWord = !endBeforeWord;
        const noNeighbourWordsWereFound = startEdgeHasNoNeighbourWord && endEdgeHasNoNeighbourWord;
        /**
         * WHEN span trimming found no neighbour words on either side
         *   CONSEQUENCE
         *     return false.
         *
         * TRUE MEANS
         *   At least one bookmark was re-laid to word boundaries.
         */
        if (noNeighbourWordsWereFound) return false;
        const { context } = this;
        const limit = this.config.searchLimit;
        let anyBookmarkRelaid = false;
        for (const name of bookmarkNames) {
            const range = context.document.getBookmarkRangeOrNullObject(name);
            range.load("isNullObject");
            /**
             * CASES
             *   - startAfterWord exists   -> search for its prefix up to limit
             *   - startAfterWord missing  -> leave this side null
             *
             * EXAMPLE
             *   limit = 5
             *     startAfterWord.text   -> "between"
             *     slice(0, 5)           -> "betwe"
             */
            const startMatches = startAfterWord ? range.search(startAfterWord.text.slice(0, limit), { matchCase: true }) : null;
            /**
             * CASES
             *   - endBeforeWord exists   -> search for its prefix up to limit
             *   - endBeforeWord missing  -> leave this side null
             *
             * EXAMPLE
             *   limit = 4
             *     endBeforeWord.text   -> "closing"
             *     slice(0, 4)          -> "clos"
             */
            const endMatches = endBeforeWord ? range.search(endBeforeWord.text.slice(0, limit), { matchCase: true }) : null;
            startMatches?.load("items");
            endMatches?.load("items");
            /**
             * WHEN this bookmark names nothing in the document
             *   CONSEQUENCE
             *     Skip it and carry on with the remaining names.
             *
             * WHY
             *   `isNullObject` cannot be read until the sync completes, and the same sync
             *   carries the `search` calls queued against that range, so a missing bookmark
             *   fails the sync before the guard below can run. The sync is per bookmark, so
             *   catching it here costs this name and no other — without the catch, one
             *   missing bookmark abandoned every name after it in this call and returned no
             *   result at all.
             */
            try {
                await context.sync();
            } catch {
                continue;
            }
            /**
             * WHEN the bookmark disappeared before search results were applied
             *   CONSEQUENCE
             *     continue.
             *
             * WHY
             *   There is no live span left to re-lay.
             */
            if (range.isNullObject) continue;
            const startMatch = startMatches && startAfterWord ? startMatches.items[startAfterWord.occurrenceIndex] : null;
            const endMatch = endMatches && endBeforeWord ? endMatches.items[endBeforeWord.occurrenceIndex] : null;
            const startNeighbourWordWasNotMatched = !startMatch;
            const endNeighbourWordWasNotMatched = !endMatch;
            const noNeighbourWordsWereMatched = startNeighbourWordWasNotMatched && endNeighbourWordWasNotMatched;
            /**
             * WHEN neither neighbour word was found inside the current range
             *   CONSEQUENCE
             *     continue.
             *
             * WHY
             *   There is no reliable word boundary to move either edge to.
             */
            if (noNeighbourWordsWereMatched) continue;
            const startRange = startMatch ? startMatch.getRange(Word.RangeLocation.end) : range.getRange(Word.RangeLocation.start);
            const endRange = endMatch ? endMatch.getRange(Word.RangeLocation.start) : range.getRange(Word.RangeLocation.end);
            startRange.expandTo(endRange).insertBookmark(name);
            await context.sync();
            anyBookmarkRelaid = true;
            AnchorLog.record({
                kind: "repair",
                bookmarkNames: [name],
                fix: "edges_moved_to_words",
                startAfterWord: startAfterWord ? startAfterWord.text : null,
                endBeforeWord: endBeforeWord ? endBeforeWord.text : null,
            });
        }
        return anyBookmarkRelaid;
    }

    /**
     * Lays every bookmark over the content of the given paragraphs, looked up by
     * `uniqueLocalId`; paragraphs that no longer exist are skipped.
     *
     * RETURNS
     *   true when the bookmarks were written; false when none of the paragraphs is in
     *   Word any more, so the caller records its recovery as not found instead of a
     *   relocation no click can select (T20, 2026-10-16).
     */
    async layOver(bookmarkNames: string[], paragraphs: WordParagraph[]): Promise<boolean> {
        const found: Word.Paragraph[] = [];
        for (const paragraph of paragraphs) {
            const wordParagraph = await OfficeDocumentBookmarks.wordParagraphById(this.context, paragraph.uniqueLocalId);
            /**
             * WHEN the paragraph still exists in Word
             *   CONSEQUENCE
             *     add it to found.
             *
             * WHY
             *   Deleted paragraph ids are expected during re-anchoring and cannot be laid
             *   over.
             */
            if (wordParagraph) found.push(wordParagraph);
        }
        /**
         * WHEN every saved paragraph id is gone
         *   CONSEQUENCE
         *     false: no surviving paragraph content remains to lay any bookmark over, and
         *     nothing is written.
         */
        if (found.length === 0) return false;
        /**
         * EXAMPLE
         *   3 surviving paragraphs
         *     found[0]  -> seed range with paragraph 1 content
         *     found[1]  -> expand across paragraph 2 content
         *     found[2]  -> expand across paragraph 3 content
         */
        let range = found[0].getRange(Word.RangeLocation.content);
        for (let index = 1; index < found.length; index++) {
            const paragraphContentRange = found[index].getRange(Word.RangeLocation.content);
            range = range.expandTo(paragraphContentRange);
        }
        for (const name of bookmarkNames) range.insertBookmark(name);
        await this.context.sync();
        return true;
    }

    /**
     * The bookmarks Word no longer finds anywhere in the document, asked of Word in one sync:
     * the user deleted their row, cell or paragraph.
     *
     * WHY Word is asked, not a read of the bookmarks' range
     *   Word exports a range that crosses table rows as its first row alone, so a cell of a
     *   later row is missing from that read while it stands in the document. Judged by the
     *   read, a table card whose three rows were all written read "The rows … are no longer
     *   in the document" (§39, -26 at a5abfd4e1, 2026-10-19).
     *
     * EXAMPLE
     *   [_q1, _q2, _q3] with row 3 deleted -> Set { _q3 }
     */
    async bookmarkNamesNotFoundInDocument(bookmarkNames: string[]): Promise<Set<string>> {
        const { context } = this;
        const bookmarkRangeLookups = bookmarkNames.map((bookmarkName) => {
            const bookmarkRange = context.document.getBookmarkRangeOrNullObject(bookmarkName);
            bookmarkRange.load("isNullObject");
            return { bookmarkName, bookmarkRange };
        });
        await context.sync();
        return new Set(bookmarkRangeLookups.filter((bookmarkRangeLookup) => bookmarkRangeLookup.bookmarkRange.isNullObject).map((bookmarkRangeLookup) => bookmarkRangeLookup.bookmarkName));
    }

    async delete(bookmarkNames: string[]): Promise<void> {
        const { context } = this;
        for (const name of bookmarkNames) {
            const range = context.document.getBookmarkRangeOrNullObject(name);
            range.load("isNullObject");
            await context.sync();
            /**
             * WHEN the bookmark is already absent
             *   CONSEQUENCE
             *     continue.
             *
             * WHY
             *   Delete only calls Word for bookmarks that still exist.
             */
            if (range.isNullObject) continue;
            context.document.deleteBookmark(name);
            await context.sync();
        }
    }

    /**
     * Reads every bookmark's own range text in one sync and keeps the names whose text
     * holds no word, read through the engine's tokenizer.
     *
     * CASES
     *   - Word no longer holds the bookmark          -> left out
     *   - its range text holds no word (an emptied
     *     paragraph's point, or only spaces)         -> kept
     *   - its range text holds a word                -> left out
     *
     * EXAMPLE
     *   bookmark          range text        result
     *   _dioptra_p_fee    ""                kept
     *   _dioptra_p_term   "The term is …"   left out
     */
    async namesHoldingNoWord(bookmarkNames: string[]): Promise<string[]> {
        return (await this.statesOf(bookmarkNames)).holdingNoWord;
    }

    /**
     * Every bookmark's own range text, read in one sync: the names Word holds nowhere, and the
     * names it holds whose text holds no word, read through the engine's tokenizer.
     *
     * CASES, per bookmark
     *   - Word no longer holds it                       -> `absent`
     *   - its range text holds no word (an emptied
     *     paragraph's point, or only spaces)            -> `holdingNoWord`
     *   - its range text holds a word                   -> neither
     *
     * EXAMPLE
     *   bookmark          range text        state
     *   _dioptra_p_fee    ""                holdingNoWord
     *   _dioptra_p_term   "The term is …"   neither
     *   _dioptra_p_gone   (not in Word)     absent
     */
    async statesOf(bookmarkNames: string[]): Promise<BookmarkStates> {
        const { context } = this;
        const bookmarkRanges = bookmarkNames.map((bookmarkName) => ({ bookmarkName, range: context.document.getBookmarkRangeOrNullObject(bookmarkName) }));
        bookmarkRanges.forEach(({ range }) => range.load("isNullObject,text"));
        await context.sync();
        return {
            absent: bookmarkRanges.filter(({ range }) => range.isNullObject).map(({ bookmarkName }) => bookmarkName),
            holdingNoWord: bookmarkRanges.filter(({ range }) => !range.isNullObject && WordTokenizer.words(range.text).length === 0).map(({ bookmarkName }) => bookmarkName),
        };
    }

    /**
     * Lays every placement's bookmarks over the content of its paragraphs, looked up by
     * `uniqueLocalId`, all queued and written in one sync.
     *
     * WHY no look-up before the write
     *   `layOver` asks Word for each paragraph first, because a paragraph the tracker remembered may
     *   be gone. The paragraphs handed here came from a read of this same session, which holds
     *   Word's write lock, so each is in Word when the write runs.
     *
     * EXAMPLE
     *   [{ _a, [P1] }, { _b _c, [P2, P3] }]
     *   -> _a over P1's content; _b and _c over P2's content expanded to P3's; one sync
     */
    async layOverTogether(placements: BookmarkPlacement[]): Promise<void> {
        const { context } = this;
        let queuedAnyBookmark = false;
        for (const { bookmarkNames, paragraphs } of placements) {
            /** WHEN a placement names no paragraph CONSEQUENCE there is nothing to lay it over: skipped. */
            if (paragraphs.length === 0) continue;
            const contentRanges = paragraphs.map((paragraph) => context.document.getParagraphByUniqueLocalId(paragraph.uniqueLocalId).getRange(Word.RangeLocation.content));
            /**
             * EXAMPLE
             *   3 paragraphs -> contentRanges[0] expanded to [1], then to [2]
             */
            const coveringRange = contentRanges.slice(1).reduce((expanded, contentRange) => expanded.expandTo(contentRange), contentRanges[0]);
            for (const name of bookmarkNames) {
                coveringRange.insertBookmark(name);
                queuedAnyBookmark = true;
            }
        }
        /** WHEN nothing was queued CONSEQUENCE no sync: Word is not called for an empty write. */
        if (!queuedAnyBookmark) return;
        await context.sync();
    }

    /** `getParagraphByUniqueLocalId` throws for a deleted paragraph; here that is a normal outcome. */
    static async wordParagraphById(context: Word.RequestContext, uniqueLocalId: string): Promise<Word.Paragraph | null> {
        try {
            const paragraph = context.document.getParagraphByUniqueLocalId(uniqueLocalId);
            paragraph.load("text,uniqueLocalId");
            await context.sync();
            return paragraph;
        } catch {
            return null;
        }
    }
}
